<script setup>
import { computed, ref, watch, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useUVXFeaturesAccess } from 'dashboard/composables/wgpt/useUVXFeaturesAccess';

import SettingIntroBanner from 'dashboard/components/widgets/SettingIntroBanner.vue';
import Button from 'next/button/Button.vue';
import BasicSettings from './basic/Index.vue';
import KnowledgeBase from './knowledgeBase/Index.vue';
import PreChat from './preChat/Index.vue';
import Workflows from './workflows/Index.vue';
import Inboxes from './inboxes/Index.vue';
import Tools from './tools/Index.vue';
import KnowledgeManager from './knowledgeManager/Index.vue';
import Quality from './quality/Index.vue';
import SpinnerLoader from 'dashboard/components-next/spinner/Spinner.vue';
import AuditTrailsDialog from './auditTrails/BotAuditTrailsDialog.vue';

const route = useRoute();
const router = useRouter();
const store = useStore();
const { t } = useI18n();
const { hasUVXFeaturesAccess } = useUVXFeaturesAccess();

const auditTrailsDialogRef = ref(null);

const routeName = computed(() => route.name);
const tabName = computed(() => route.params.tab);
const botId = computed(() => Number(route.params.botId));
const bot = computed(() => store.getters['wgptBots/getBot'](botId.value));
const fetchedBotId = ref(null);
const isLoading = computed(
  () => bot.value.id !== botId.value || fetchedBotId.value !== botId.value
);

const tabs = [
  {
    key: 'basic',
    name: t('WGPT_BOTS.EDIT.TABS.BASIC.NAME'),
  },
  {
    key: 'inboxes',
    name: t('WGPT_BOTS.EDIT.TABS.INBOXES.NAME'),
  },
  {
    key: 'pre_chat',
    name: t('WGPT_BOTS.EDIT.TABS.PRE_CHAT.NAME'),
  },
  {
    key: 'knowledge_base',
    name: t('WGPT_BOTS.EDIT.TABS.KNOWLEDGE_BASE.NAME'),
    uvxOnly: true,
  },
  {
    key: 'workflows',
    name: t('WGPT_BOTS.EDIT.TABS.WORKFLOWS.NAME'),
    uvxOnly: true,
  },
  {
    key: 'toolsets',
    name: t('WGPT_BOTS.EDIT.TABS.TOOLS.NAME'),
    uvxOnly: true,
  },
  {
    key: 'knowledge_manager',
    name: t('WGPT_BOTS.EDIT.TABS.KNOWLEDGE_MANAGER.NAME'),
  },
  {
    key: 'quality',
    name: t('WGPT_BOTS.EDIT.TABS.QUALITY.NAME'),
    uvxOnly: true,
  },
];

const filteredTabs = computed(() => {
  return tabs.filter(tab => {
    if (hasUVXFeaturesAccess.value) return true;
    return !tab.uvxOnly;
  });
});

const selectedTabIndex = computed(() => {
  return Math.max(
    0,
    filteredTabs.value.findIndex(tab => tab.key === tabName.value)
  );
});

const showAuditTrailButton = computed(() => {
  return !route.params.toolId;
});

const fetchBot = async () => {
  fetchedBotId.value = null;
  await store.dispatch('wgptBots/show', botId.value);
  fetchedBotId.value = botId.value;
};

const onTabChange = index => {
  const { params } = route;
  router.replace({
    name: 'wgpt_bots_edit',
    params: {
      ...params,
      tab: filteredTabs.value[index].key,
    },
  });
};

const openAuditTrails = () => {
  auditTrailsDialogRef.value.open(bot.value);
};

watch(routeName, name => {
  if (name === 'wgpt_bots_edit') {
    fetchBot();
  }
});

onMounted(() => {
  fetchBot();
});
</script>

<template>
  <div
    class="grid grid-rows-[auto_1fr] h-full flex-grow flex-shrink pr-0 pl-0 w-full min-w-0 settings"
  >
    <SettingIntroBanner :header-image="bot.avatarUrl" :header-title="bot.name">
      <Button
        v-if="showAuditTrailButton"
        v-tooltip.left="t('WGPT_BOTS.EDIT.TOOLTIPS.AUDIT_TRAIL')"
        ghost
        slate
        sm
        icon="i-lucide-history"
        class="absolute z-10 end-4 top-2"
        @click="openAuditTrails"
      />
      <woot-tabs
        class="[&_ul]:p-0 top-px relative"
        :index="selectedTabIndex"
        :border="false"
        @change="onTabChange"
      >
        <woot-tabs-item
          v-for="(tab, index) in filteredTabs"
          :key="tab.key"
          :index="index"
          :name="tab.name"
          :show-badge="false"
          is-compact
        />
      </woot-tabs>
    </SettingIntroBanner>

    <div
      v-if="isLoading"
      class="flex items-center justify-center h-full w-full"
    >
      <SpinnerLoader :size="28" class="text-n-blue-9" />
    </div>
    <section v-else class="w-full overflow-auto">
      <Workflows
        v-if="tabName === 'workflows' && hasUVXFeaturesAccess"
        :bot="bot"
      />

      <KnowledgeManager
        v-else-if="tabName === 'knowledge_manager'"
        class="h-full"
      />

      <Quality
        v-else-if="tabName === 'quality' && hasUVXFeaturesAccess"
        :bot="bot"
        class="h-full overflow-hidden"
      />

      <div v-else class="max-w-7xl mx-auto w-full py-8">
        <Inboxes v-if="tabName === 'inboxes'" :bot="bot" />

        <PreChat v-else-if="tabName === 'pre_chat'" :bot="bot" />

        <KnowledgeBase
          v-else-if="tabName === 'knowledge_base' && hasUVXFeaturesAccess"
          :bot="bot"
        />

        <Tools
          v-else-if="tabName === 'toolsets' && hasUVXFeaturesAccess"
          :bot="bot"
        />

        <BasicSettings v-else :bot="bot" />
      </div>
    </section>

    <AuditTrailsDialog ref="auditTrailsDialogRef" />
  </div>
</template>
